import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, TextInput } from '@mantine/core';
import { SlLocationPin } from 'react-icons/sl';
import { v4 as uuidv4 } from 'uuid';
import { baseUrl } from '../../Constants/constant';
import useFetch from '../../Customhooks/useFetch';

interface SelectOption {
    value: string;
    label: string;
    place_id: string;
}

interface LocationAutocompleteProps {
    label?: string;
    placeholder?: string;
    withAsterisk?: boolean;
    value?: string;
    onChange?: (value: string | null, placeId?: string) => void;
    error?: string | null;
    variant?: string;
    radius?: number;
    leftSection?: React.ReactNode;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    style?: React.CSSProperties;
    latLngMode?: boolean;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
    label = "Location",
    placeholder = "Enter location",
    withAsterisk = false,
    value = "",
    onChange,
    error = null,
    variant = "filled",
    radius = 6,
    leftSection = <SlLocationPin size={17} />,
    disabled = false,
    required = false,
    className,
    style,
    latLngMode = false,
}) => {
    const [location, setLocation] = useState<SelectOption[]>([]);
    const [search, setSearch] = useState('');
    const [sendsearch, setSendsearch] = useState('');
    const [latLngValue, setLatLngValue] = useState('');

    const token = localStorage.getItem("token");

    const fetchConfig = useMemo(() => ({
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            token: token,
        },
    }), [token]);

    const { data: getLocationData, fetchData: fetchLocation } = useFetch(fetchConfig);
    const { data: getLocationAdvancedData, fetchData: fetchLocationAdvanced } = useFetch(fetchConfig);
    const { data: getLogisticsHubsData, fetchData: fetchLogisticsHubs } = useFetch(fetchConfig);
    const { data: getPlaceIdData, fetchData: fetchPlaceId } = useFetch(fetchConfig);

    const sessionTokenRef = useRef(uuidv4());
    const searchAttemptRef = useRef(0);
    const lastSearchRef = useRef('');
    const currentTypeRef = useRef('establishment');

useEffect(() => {
    if (getPlaceIdData && latLngValue) {
        if (getPlaceIdData?.status === 400 || getPlaceIdData?.errors) return;
        const placeId = getPlaceIdData?.data?.placeId;
        const placeName = getPlaceIdData?.data?.placeName;
        setLatLngValue(placeName || latLngValue);
        onChange?.(placeName || latLngValue, placeId);
    }
}, [getPlaceIdData]);


    const handleLatLngChange = (val: string) => {
        setLatLngValue(val);
        onChange?.(val, ''); // clear placeId while typing
    };

    const handleLatLngBlur = () => {
        if (latLngValue) {
            fetchPlaceId(`${baseUrl}compute/lca/fetchLatLongPlaceId?latLng=${latLngValue}`);
        }
    };

    const performSmartLocationSearch = async (searchInput: string) => {
        if (!searchInput || searchInput.length < 2) return;
        if (lastSearchRef.current === searchInput) return;

        lastSearchRef.current = searchInput;
        searchAttemptRef.current = 0;
        currentTypeRef.current = 'establishment';
        const sessionToken = sessionTokenRef.current;
        try {
            fetchLocation(`${baseUrl}compute/lca/fetchLocation?input=${searchInput}&sessiontoken=${sessionToken}&types=establishment`);
            searchAttemptRef.current = 1;
        } catch (error) {
            tryAdvancedSearch(searchInput, sessionToken);
        }
    };

    const tryAdvancedSearch = async (searchInput: string, sessionToken: string) => {
        if (searchAttemptRef.current >= 2) return;
        try {
            fetchLocationAdvanced(`${baseUrl}compute/lca/fetchLocationAdvanced?input=${searchInput}&sessiontoken=${sessionToken}&types=geocode`);
            searchAttemptRef.current = 2;
        } catch (error) {
            tryLogisticsSearch(searchInput, sessionToken);
        }
    };

    const tryLogisticsSearch = async (searchInput: string, sessionToken: string) => {
        if (searchAttemptRef.current >= 3) return;
        try {
            fetchLogisticsHubs(`${baseUrl}compute/lca/fetchLogisticsHubs?input=${searchInput}&sessiontoken=${sessionToken}&hubType=port`);
            searchAttemptRef.current = 3;
        } catch (error) {
            tryCitiesSearch(searchInput, sessionToken);
        }
    };

    const tryCitiesSearch = async (searchInput: string, sessionToken: string) => {
        if (searchAttemptRef.current >= 4) return;
        try {
            fetchLocation(`${baseUrl}compute/lca/fetchLocation?input=${searchInput}&sessiontoken=${sessionToken}&types=(cities)`);
            searchAttemptRef.current = 4;
        } catch (error) {
            console.error('Cities search failed:', error);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (search && search.length > 2) {
                setSendsearch(search);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        if (sendsearch) {
            performSmartLocationSearch(sendsearch);
        }
    }, [sendsearch]);

    useEffect(() => {
        if (searchAttemptRef.current === 1 && getLocationData) {
            const resultCount = getLocationData?.data?.predictions?.length || 0;
            if (resultCount < 8) {
                tryAdvancedSearch(lastSearchRef.current, sessionTokenRef.current);
            }
        }
    }, [getLocationData]);

    useEffect(() => {
        if (searchAttemptRef.current === 2 && getLocationAdvancedData) {
            const resultCount = getLocationAdvancedData?.data?.predictions?.length || 0;
            if (resultCount < 10) {
                tryLogisticsSearch(lastSearchRef.current, sessionTokenRef.current);
            }
        }
    }, [getLocationAdvancedData]);

    useEffect(() => {
        if (searchAttemptRef.current === 3 && getLogisticsHubsData) {
            const resultCount = getLogisticsHubsData?.data?.predictions?.length || 0;
            if (resultCount < 3) {
                tryCitiesSearch(lastSearchRef.current, sessionTokenRef.current);
            }
        }
    }, [getLogisticsHubsData]);

    useEffect(() => {
        let allLocationOptions: SelectOption[] = [];
        const standardResults = getLocationData?.data?.predictions || [];
        const advancedResults = getLocationAdvancedData?.data?.predictions || [];
        const logisticsResults = getLogisticsHubsData?.data?.predictions || [];
        const allResults = [...standardResults, ...advancedResults, ...logisticsResults];
        const uniqueResults = allResults.filter((result, index, self) =>
            index === self.findIndex(r => r?.description === result?.description)
        );
        if (uniqueResults.length > 0) {
            allLocationOptions = uniqueResults.map((el: any, index: number) => ({
                value: index.toString(),
                label: el?.description || el?.structured_formatting?.main_text || 'Unknown Location',
                place_id: el?.place_id
            }));
        }
        setLocation(allLocationOptions);
    }, [getLocationData, getLocationAdvancedData, getLogisticsHubsData]);

    const handleChange = (selectedValue: string | null) => {
        setSearch(selectedValue || '');
        if (selectedValue) {
            const selectedOption = location.find(item => item.label === selectedValue);
            const placeId = selectedOption?.place_id || '';
            onChange?.(selectedValue, placeId);
        } else {
            onChange?.(null);
        }
    };

    if (latLngMode) {
        return (
            <TextInput
                label={label}
                placeholder="eg. 20.23,23.45"
                withAsterisk={withAsterisk}
                variant={variant as any}
                radius={radius}
                value={latLngValue}
                onChange={(e) => handleLatLngChange(e.target.value)}
                onBlur={handleLatLngBlur}
                error={error}
                disabled={disabled}
                className={className}
                style={style}
            />
        );
    }

    return (
        <Autocomplete
            label={label}
            placeholder={placeholder}
            variant={variant}
            withAsterisk={withAsterisk}
            required={required}
            data={location}
            value={value}
            onChange={handleChange}
            filter={({ options }) => options}
            leftSection={leftSection}
            radius={radius}
            comboboxProps={{ withinPortal: false, shadow: 'md' }}
            error={error}
            disabled={disabled}
            className={className}
            style={style}
        />
    );
};

export default LocationAutocomplete;